import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Download, LogOut, RefreshCcw } from "lucide-react";

/**
 * MFA gate:
 *   - If user hasn't enrolled → show setup (QR + verify) and reveal 8 backup codes on success.
 *   - If user has enrolled but this session isn't verified → show verify screen.
 *
 * Once mfa_verified=true on the session, we redirect to /.
 */
export default function MfaGate() {
  const { user, refresh, logout, loading } = useAuth();
  const navigate = useNavigate();

  const enrolled = !!user?.mfa_enrolled;
  const verified = !!user?.mfa_verified;

  // If we're already verified, bounce to dashboard right away.
  useEffect(() => {
    if (!loading && verified) navigate("/", { replace: true });
  }, [loading, verified, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center text-[#475569] text-sm">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center px-6 py-10">
      <div
        className="w-full max-w-md bg-white border border-[#e2e8f0] rounded-2xl shadow-sm p-8"
        data-testid="mfa-gate"
      >
        <div className="flex items-center gap-2 text-[#0284c7] mb-2">
          <ShieldCheck className="w-5 h-5" />
          <span className="text-[11px] font-semibold tracking-[0.14em] uppercase">
            {enrolled ? "Two-factor sign in" : "Set up two-factor sign in"}
          </span>
        </div>
        <h1 className="font-heading text-2xl font-semibold text-[#0f172a] leading-tight">
          {enrolled ? "Enter your 6-digit code" : "Protect this account with Google Authenticator"}
        </h1>
        <p className="text-sm text-[#475569] mt-2">
          Signed in as <span className="font-mono">{user.email}</span>.
        </p>

        {enrolled ? (
          <VerifyForm onSuccess={async () => { await refresh(); navigate("/", { replace: true }); }} />
        ) : (
          <EnrollFlow onDone={async () => { await refresh(); navigate("/", { replace: true }); }} />
        )}

        <div className="mt-6 pt-4 border-t border-[#e2e8f0] flex justify-between text-xs">
          <button
            type="button"
            className="text-[#475569] hover:text-[#0f172a] inline-flex items-center gap-1"
            onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
            data-testid="mfa-signout"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function EnrollFlow({ onDone }) {
  const [qrUrl, setQrUrl] = useState(null);
  const [provisioningUri, setProvisioningUri] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    let objectUrl = null;
    (async () => {
      try {
        const res = await api.post("/mfa/setup/initiate", null, {
          withCredentials: true,
          responseType: "blob",
        });
        objectUrl = URL.createObjectURL(res.data);
        setQrUrl(objectUrl);
        const uri = res.headers?.["x-mfa-provisioning-uri"] || "";
        setProvisioningUri(uri);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to start MFA setup.");
      }
    })();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, []);

  const secret = useMemo(() => {
    try {
      const u = new URL(provisioningUri);
      return new URLSearchParams(u.search).get("secret") || "";
    } catch {
      return "";
    }
  }, [provisioningUri]);

  const submit = async () => {
    setError("");
    if (!/^\d{6}$/.test(code)) { setError("Enter the 6-digit code."); return; }
    setBusy(true);
    try {
      const res = await api.post("/mfa/setup/verify", { code }, { withCredentials: true });
      setBackupCodes(res.data.backup_codes || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Invalid code. Try the next one shown in the app.");
    } finally {
      setBusy(false);
    }
  };

  const downloadCodes = () => {
    const blob = new Blob(
      [
        `Rounds — Backup codes for ${new Date().toLocaleString()}\n\n` +
        backupCodes.map((c, i) => `${i + 1}. ${c}`).join("\n") +
        `\n\nKeep these safe. Each code works exactly ONCE if you lose access to your authenticator app.\n`,
      ],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rounds-backup-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (backupCodes) {
    return (
      <div className="mt-6" data-testid="mfa-backup-codes">
        <p className="text-sm text-[#0f172a]">
          <strong>Save these 8 backup codes.</strong> Each can be used ONCE if you lose your phone.
          They will never be shown again.
        </p>
        <ul className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm bg-[#f7f4ec] border border-[#e2e8f0] rounded-lg p-3">
          {backupCodes.map((c, i) => (
            <li key={i} data-testid={`backup-code-${i}`}>{c}</li>
          ))}
        </ul>
        <div className="mt-4 flex gap-2">
          <Button
            onClick={downloadCodes}
            className="bg-[#0284c7] hover:bg-[#21473c] text-white flex-1"
            data-testid="mfa-download-codes"
          >
            <Download className="w-4 h-4 mr-2" /> Download .txt
          </Button>
          <Button
            variant="outline"
            onClick={onDone}
            className="border-[#e2e8f0] flex-1"
            data-testid="mfa-continue"
          >
            I saved them, continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-4">
      <ol className="list-decimal list-inside text-sm text-[#0f172a] space-y-1">
        <li>Install <strong>Google Authenticator</strong> (or any TOTP app).</li>
        <li>Scan the QR code below.</li>
        <li>Enter the 6-digit code the app shows.</li>
      </ol>
      <div className="flex justify-center py-2">
        {qrUrl ? (
          <img
            src={qrUrl}
            alt="Scan with your authenticator app"
            width={200}
            height={200}
            className="border border-[#e2e8f0] rounded-lg"
            data-testid="mfa-qr-image"
          />
        ) : (
          <div className="w-[200px] h-[200px] bg-[#f1f5f9] rounded-lg animate-pulse" />
        )}
      </div>
      {secret && (
        <details className="text-xs text-[#475569]">
          <summary className="cursor-pointer">Can&apos;t scan? Enter this secret manually.</summary>
          <div className="mt-2 font-mono select-all break-all p-2 rounded bg-[#f1f5f9]" data-testid="mfa-secret">
            {secret}
          </div>
        </details>
      )}
      <div>
        <Label htmlFor="mfa-code" className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
          6-digit code
        </Label>
        <Input
          id="mfa-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="123 456"
          className="mt-1 border-[#e2e8f0] tracking-widest text-center font-mono text-lg"
          data-testid="mfa-code-input"
        />
      </div>
      {error && <p className="text-sm text-[#b94a48]" data-testid="mfa-error">{error}</p>}
      <Button
        onClick={submit}
        disabled={busy || !qrUrl}
        className="w-full bg-[#0284c7] hover:bg-[#21473c] text-white"
        data-testid="mfa-verify-button"
      >
        {busy ? "Verifying…" : "Verify & enable"}
      </Button>
    </div>
  );
}

function VerifyForm({ onSuccess }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!code.trim()) { setError("Enter your 6-digit code, or a backup code."); return; }
    setBusy(true);
    try {
      const res = await api.post("/mfa/login/verify", { code: code.trim() }, { withCredentials: true });
      if (res.data?.used_backup_code) {
        toast.warning("Backup code used. Consider regenerating your backup list.");
      } else {
        toast.success("Verified.");
      }
      await onSuccess();
    } catch (e) {
      setError(e?.response?.data?.detail || "Invalid code.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 space-y-4">
      <div>
        <Label htmlFor="mfa-verify-code" className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
          6-digit code (or backup code)
        </Label>
        <Input
          id="mfa-verify-code"
          inputMode="text"
          autoComplete="one-time-code"
          maxLength={16}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="123 456  or  ABC12-3DEFG"
          className="mt-1 border-[#e2e8f0] tracking-widest text-center font-mono text-lg"
          data-testid="mfa-verify-code-input"
          autoFocus
        />
      </div>
      {error && <p className="text-sm text-[#b94a48]" data-testid="mfa-verify-error">{error}</p>}
      <Button
        onClick={submit}
        disabled={busy}
        className="w-full bg-[#0284c7] hover:bg-[#21473c] text-white"
        data-testid="mfa-verify-submit"
      >
        {busy ? "Verifying…" : "Verify"}
      </Button>
      <button
        type="button"
        onClick={() => { setCode(""); setError(""); }}
        className="w-full text-xs text-[#475569] hover:text-[#0f172a] inline-flex items-center justify-center gap-1"
        data-testid="mfa-verify-clear"
      >
        <RefreshCcw className="w-3.5 h-3.5" /> Clear
      </button>
    </div>
  );
}
